'use client';

import React, { useEffect } from 'react';
import { cm } from 'src/utils/classNames';
import { LoadingChart } from './Charts/LoadingChart';
import {
	BigNum,
	BN,
	calculateBorrowRate,
	calculateDepositRate,
	calculateUtilization,
	PERCENTAGE_PRECISION_EXP,
	SPOT_MARKET_UTILIZATION_PRECISION_EXP,
	SpotMarketAccount,
} from '@velocity-exchange/sdk';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useDriftClient from 'src/hooks/useDriftClient';
import { ZERO_BIGNUM } from 'src/constants/math';
import NumberDisplay from './NumberDisplay/NumberDisplay';

import {
	Line,
	LineChart,
	CartesianGrid,
	ResponsiveContainer,
	XAxis,
	YAxis,
	Tooltip as ChartsTooltip,
	Legend,
	ReferenceLine,
	TooltipProps,
	LabelProps,
} from 'recharts';

import type { CartesianViewBox } from 'recharts/types/util/types';
import { getApyFromApr } from 'src/utils/borrowLend';
import { getColorVar } from 'src/utils/theme/colorVars';
import { calculateYAxisWidth } from 'src/utils/graph';

type UtilizationDataPoint = {
	utilization: number;
	depositRate: number;
	borrowRate: number;
};

const dataKeyLabelsMap = {
	depositRate: 'Deposit Rate',
	borrowRate: 'Borrow Rate',
};

const CustomizedLabel = (props: LabelProps) => {
	const { value } = props;
	const viewBox = props.viewBox as CartesianViewBox;

	const viewBoxX = viewBox.x ?? 0;
	const viewBoxHeight = viewBox.height ?? 0;
	const x = Number(value) < 45 ? viewBoxX + 10 : viewBoxX - 10;

	return (
		<text
			x={x}
			y={viewBoxHeight / 2}
			fill={getColorVar('text-secondary')}
			fontSize={10}
			textAnchor={Number(value) < 45 ? 'start' : 'end'}
		>
			Utilization {Number(value).toFixed(2)}%
		</text>
	);
};

// Generate utilization to borrow rate data
const generateBorrowLendChartData = (
	spotMarketAccount: SpotMarketAccount,
	currentUtilization: BigNum,
	numPoints = 20
): UtilizationDataPoint[] => {
	const data = [];
	for (let i = 0; i <= numPoints; i++) {
		const utilization = BigNum.fromPrint(
			`${i / numPoints}`,
			SPOT_MARKET_UTILIZATION_PRECISION_EXP
		);
		const borrowRate = BigNum.from(
			calculateBorrowRate(spotMarketAccount, undefined, utilization.val),
			PERCENTAGE_PRECISION_EXP
		);
		const depositRate = BigNum.from(
			calculateDepositRate(spotMarketAccount, undefined, utilization.val),
			PERCENTAGE_PRECISION_EXP
		);
		data.push({
			utilization: utilization.mul(new BN(100)).toNum(),
			borrowRate: getApyFromApr(borrowRate.mul(new BN(100)).toNum(), 'hourly'),
			depositRate: getApyFromApr(
				depositRate.mul(new BN(100)).toNum(),
				'hourly'
			),
		});

		// Add in the current utilization point
		if (
			currentUtilization.toNum() > i / numPoints &&
			currentUtilization.toNum() < (i + 1) / numPoints
		) {
			const utilizedBorrowRate = BigNum.from(
				calculateBorrowRate(
					spotMarketAccount,
					undefined,
					currentUtilization.val
				),
				PERCENTAGE_PRECISION_EXP
			);
			const utilizedDepositRate = BigNum.from(
				calculateDepositRate(
					spotMarketAccount,
					undefined,
					currentUtilization.val
				),
				PERCENTAGE_PRECISION_EXP
			);
			data.push({
				utilization: currentUtilization.mul(new BN(100)).toNum(),
				borrowRate: utilizedBorrowRate.mul(new BN(100)).toNum(),
				depositRate: utilizedDepositRate.mul(new BN(100)).toNum(),
			});
		}
	}
	return data;
};

const CustomTooltip = ({ active, payload }: TooltipProps<any, any>) => {
	if (active && payload && payload.length) {
		return (
			<div className="flex flex-col gap-y-xs rounded-md bg-tooltip-bg p-sm text-text-default">
				<div className="flex w-full items-center justify-between gap-x-sm">
					<span className="typo-body-xs text-text-default">Utilization</span>
					<NumberDisplay
						value={BigNum.fromPrint(
							`${payload?.[0]?.payload?.utilization ?? 0}`,
							PERCENTAGE_PRECISION_EXP
						)}
						type="percentage"
					/>
				</div>
				{payload.map((item) => (
					<div
						className="flex w-full flex-row items-center justify-between gap-x-sm"
						key={JSON.stringify(item)}
					>
						<span className="typo-body-xs text-text-default">
							{dataKeyLabelsMap[
								item.dataKey as keyof typeof dataKeyLabelsMap
							] ?? item.dataKey}
						</span>
						<span
							className={cm(
								'typo-body-xs',
								item.dataKey === 'depositRate' &&
									'text-text-positive-green-button',
								item.dataKey === 'borrowRate' && 'text-star-yellow'
							)}
						>
							<NumberDisplay
								value={BigNum.fromPrint(
									`${item.value}`,
									PERCENTAGE_PRECISION_EXP
								)}
								type="percentage"
							/>
						</span>
					</div>
				))}
			</div>
		);
	}

	return null;
};

const BorrowLendRateToUtilizationChart = (props: {
	//timeframeIndex: number;
	marketIndex: number;
	className?: string;
	chartClassName?: string;
	loadingClassName?: string;
}) => {
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();
	const [isLoading, setIsLoading] = React.useState(true);
	const [chartData, setChartData] = React.useState<UtilizationDataPoint[]>([]);
	const [currentUtilization, setCurrentUtilization] =
		React.useState(ZERO_BIGNUM);
	const [optimalUtilization, setOptimalUtilization] =
		React.useState(ZERO_BIGNUM);

	useEffect(() => {
		if (driftClient && driftClientIsReady) {
			const spotMarketAccount = driftClient.getSpotMarketAccount(
				props.marketIndex
			);
			if (!spotMarketAccount) return;

			const utilization = BigNum.from(
				calculateUtilization(spotMarketAccount),
				SPOT_MARKET_UTILIZATION_PRECISION_EXP
			);
			const chartData = generateBorrowLendChartData(
				spotMarketAccount,
				utilization
			);
			setCurrentUtilization(utilization);
			setOptimalUtilization(
				BigNum.from(
					new BN(spotMarketAccount.optimalUtilization),
					SPOT_MARKET_UTILIZATION_PRECISION_EXP
				)
			);
			setChartData(chartData);
			setIsLoading(false);
		}
	}, [driftClient, driftClientIsReady, props.marketIndex]);

	const maxY = chartData.length
		? Math.max(
				chartData[chartData.length - 1].borrowRate,
				chartData[chartData.length - 1].depositRate
			)
		: 0;

	// Calculating the Y ticks is kinda tricky... but if we assuming it's mostly always going to max out somewhere between 50% and 1000%, maybe just break it down into either 25%, 50%, 100%, 250% increments.
	const yTicks =
		maxY < 100
			? [0, 25, 50, 75, 100]
			: maxY <= 200
				? [0, 50, 100, 150, 200]
				: maxY <= 500
					? [0, 100, 200, 300, 400, 500]
					: [0, 250, 500, 750, 1000];

	const yAxisWidth = calculateYAxisWidth(yTicks.map((tick) => `${tick}%`));

	return (
		<div>
			<div className="flex flex-row items-center justify-between">
				<div className="flex flex-row items-center gap-1">
					<span className="typo-body-xs text-text-secondary">Utilization</span>
					<span className="typo-label-sm text-text-default">
						<NumberDisplay
							value={currentUtilization.mul(new BN(100))}
							type="percentage"
						/>
					</span>
				</div>
				<div className="flex flex-row items-center gap-1">
					<span className="typo-body-xs text-text-secondary">
						Optimal Utilization
					</span>
					<span className="typo-label-sm text-text-default">
						<NumberDisplay
							value={optimalUtilization.mul(new BN(100))}
							type="percentage"
						/>
					</span>
				</div>
			</div>
			<div
				className={cm(
					'mt-4 mb-6 min-h-[230px] w-full grow items-center justify-center',
					props.className
				)}
			>
				{isLoading ? (
					<div
						className={cm(
							`flex h-[230px] w-full flex-col items-center justify-center rounded-sm text-center`,
							props.loadingClassName
						)}
					>
						<LoadingChart />
					</div>
				) : (
					<ResponsiveContainer width="100%" height="100%">
						<LineChart data={chartData} margin={{ top: 12 }}>
							<CartesianGrid
								stroke={getColorVar('stroke-divider')}
								vertical={false}
							/>
							<YAxis
								width={yAxisWidth}
								type="number"
								domain={[0, yTicks[yTicks.length - 1]]}
								stroke={getColorVar('stroke-divider')}
								tickLine={false}
								tickMargin={4}
								ticks={yTicks}
								tickFormatter={(tick) => `${tick}%`}
							/>
							<XAxis
								type="number"
								domain={[0, 100]}
								dataKey={'utilization'}
								stroke={getColorVar('stroke-divider')}
								tickLine={false}
								tickMargin={8}
								ticks={[0, 25, 50, 75, 100]}
								tickFormatter={(tick) => `${tick}%`}
								// text css are found in index.css
							/>

							<Line
								type="monotone"
								dot={false}
								dataKey="depositRate"
								stroke={getColorVar('text-positive-green-button')}
							/>
							<Line
								type="monotone"
								dot={false}
								dataKey="borrowRate"
								stroke={getColorVar('star-yellow')}
							/>

							<ReferenceLine
								x={currentUtilization.mul(new BN(100)).toNum()}
								stroke={getColorVar('text-secondary')}
								label={
									<CustomizedLabel
										value={currentUtilization.mul(new BN(100)).toNum()}
									/>
								}
							/>

							<ChartsTooltip
								content={(tooltipProps) => <CustomTooltip {...tooltipProps} />}
								cursor={{
									stroke: getColorVar('text-secondary'),
									strokeDasharray: '4',
								}}
							/>

							<Legend
								verticalAlign="bottom"
								height={30}
								formatter={(value) =>
									`${
										dataKeyLabelsMap[value as keyof typeof dataKeyLabelsMap] ??
										value
									}`
								}
							/>
						</LineChart>
					</ResponsiveContainer>
				)}
			</div>
		</div>
	);
};

export default React.memo(BorrowLendRateToUtilizationChart);
